/**
 * Compute the value a parameter should reset to.
 * Pure helper extracted from App.clearLocalStorageVariable so it can be unit-tested.
 *
 * @param {Object} variable - The parameter variable definition.
 * @param {string|null|undefined} storedOriginalValueRaw - JSON string from
 *   localStorage key `${key}:originalVal` (the DAG.json default), or null/undefined.
 * @returns {*} The value to assign to `variable.value` on reset.
 */
import { allowsEmptyValue } from './emptyValue';

export function computeResetValue(variable, storedOriginalValueRaw) {
  // Date param whose default tracks the data's min/max: reset to the live min/max.
  if (variable.type === 'Date' && ['max_date', 'min_date'].includes(variable.optionsDateValueType)) {
    return variable.optionsDateValueType === 'max_date' ? variable.max : variable.min;
  }

  // Multi-select whose default is an explicit "Select All": reset to [] so the
  // server repopulates from the (session-filtered) options instead of restoring
  // the full unfiltered admin list. Prevents leaking unauthorized values. tam#36243
  if (variable.multivalued && variable.useAllValuesAsDefaultValues && variable.allValuesType === 'all') {
    return [];
  }

  const targetValue = storedOriginalValueRaw !== undefined && storedOriginalValueRaw !== null
    ? JSON.parse(storedOriginalValueRaw)
    : variable.value;

  // For LOV parameters, guard against exposing values outside the session-filtered
  // options list. When session parameters cascade into a LOV, `variable.options`
  // already contains only the authorized values at reset time.
  // Without this guard, restoring `_originalValue` (the admin-configured DAG.json
  // default) can reveal values the current user has no permission to see. tam#36685
  if (variable.input === 'lov') {
    // An empty default is a valid single-select value when the parameter allows
    // empty values. It is intentionally absent from the LOV options, so do not
    // fall through to the first available option during reset.
    if (!variable.multivalued && allowsEmptyValue(variable) &&
        (targetValue === '' || targetValue === null || targetValue === undefined)) {
      return targetValue;
    }

    if (variable.options && variable.options.length > 0) {
      // Options available in state: validate the reset target against them.
      const validValues = variable.options.map(o =>
        String(o.value !== undefined && o.value !== null ? o.value : o.name)
      );
      if (variable.multivalued) {
        return Array.isArray(targetValue) ? targetValue.filter(v => validValues.includes(String(v))) : [];
      } else {
        return validValues.includes(String(targetValue)) ? targetValue : validValues[0];
      }
    } else if (variable.optionsDynamic) {
      // Options are fetched from a separate LOV items cache (lovItemsCached mechanism)
      // and are not present in variable.options at reset time. We cannot validate the
      // admin default here, so keep the current session-appropriate value unchanged.
      // The server will apply session filtering on the next run. tam#36685
      return variable.value;
    }
  }

  return targetValue;
}
